import { v4 as uuid } from 'uuid';

// Request bodies for the menu editor. Each builder produces exactly what the classic editor
// (views/MenuCategory) sends for the same action, so both editors write the same data.
// Nothing here mutates its input: the arguments usually come straight from the Redux store.

export const DISH_TAGS = [
  { value: 'Hot', label: 'Hot' },
  { value: 'Spicy', label: '🌶️' },
  { value: 'Hotter', label: '🌶️🌶️' },
  { value: 'Hottest', label: '🌶️🌶️🌶️' },
  { value: 'Cold', label: 'Cold' },
  { value: 'Vegetarian', label: 'Vegetarian' },
  { value: 'Vegan', label: 'Vegan' },
  { value: 'Gluten Free', label: 'Gluten Free' },
  { value: 'Halal', label: 'Halal' },
  { value: 'Recommended', label: 'Recommended' },
  { value: 'Seasonal', label: 'Seasonal' },
  { value: 'VGO', label: 'VGO' },
  { value: 'GFO', label: 'GFO' },
  { value: 'Dairy Free', label: 'Dairy Free' },
  { value: 'Nuts Free', label: 'Nuts Free' },
  { value: 'Dukkah-NF', label: 'Dukkah-NF' },
];

export const KITCHEN_FLAGS = [
  { key: 'isTakeawayDisabled', label: 'Not for takeaway' },
  { key: 'isForPrintingDisabled', label: 'Not for category printing' },
  { key: 'isForKDSDisabled', label: 'Not for KDS' },
  { key: 'isMainCourse', label: 'Main course' },
];

/** Names of the menu's cooking categories, which may be stored as strings or `{id, name}`. */
export const cookingCategoryNames = (menu) =>
  ((menu && menu.cookingCategories) || []).map((category) =>
    typeof category === 'object' && category ? category.name : category
  );

export const joinPrintCategories = (names) => (names && names.length > 0 ? names.join(';') : null);

export const splitPrintCategories = (printCategory) => (printCategory ? printCategory.split(';') : []);

export const sortCategories = (categories) =>
  [...(categories || [])].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));

export const sortGroups = (groups) => [...(groups || [])].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));

/** Moves the element at `from` to `to` and renumbers `sort` from 0, as the classic editor's drag does. */
export const reorderWithSort = (list, from, to) => {
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next.map((entry, index) => ({ ...entry, sort: index }));
};

// ---------------------------------------------------------------------------------------------
// Categories

/** POST /menuCategory, as NewCategory sends it. */
export const newCategoryPayload = (form, menuId) => ({
  name: form.name,
  altName: form.altName,
  description: form.description,
  cookingPriority: Number(form.cookingPriority),
  active: true,
  active_begin: null,
  active_end: null,
  hidden_until: null,
  menuId,
  hideForWeb: form.hideForWeb,
  colorHex: form.colorHex,
});

/** PUT /menuCategory, as CategoryEdit sends it. */
export const editedCategoryPayload = (form, category, menuId) => ({
  name: form.name,
  description: form.description,
  cookingPriority: Number(form.cookingPriority),
  id: category.id,
  altName: form.altName,
  menuId,
  hideForWeb: form.hideForWeb,
  isHiddenForPickup: form.isHiddenForPickup,
  colorHex: form.colorHex,
});

/** PUT /menuCategories body after a category drag: every category, renumbered. */
export const categoriesInOrder = (categories, from, to) => reorderWithSort(categories, from, to);

/** Strips a deleted group from every dish and size; returns only the categories that changed. */
export const categoriesWithoutGroup = (categories, groupId) => {
  const changed = [];
  const all = categories.map((category) => {
    let touched = false;
    const items = (category.items || []).map((item) => {
      const groups = (item.groups || []).filter((group) => group.id !== groupId);
      let itemTouched = groups.length !== (item.groups || []).length;
      const sizes = (item.sizes || []).map((size) => {
        const sizeGroups = (size.groups || []).filter((group) => group.id !== groupId);
        if (sizeGroups.length === (size.groups || []).length) {
          return size;
        }
        itemTouched = true;
        return { ...size, groups: sizeGroups };
      });
      if (!itemTouched) {
        return item;
      }
      touched = true;
      return { ...item, groups, ...(item.sizes ? { sizes } : {}) };
    });
    if (!touched) {
      return category;
    }
    const next = { ...category, items };
    changed.push(next);
    return next;
  });
  return { all, changed };
};

// ---------------------------------------------------------------------------------------------
// Dishes

export const emptyDishForm = () => ({
  name: '',
  altName: '',
  shortName: '',
  description: '',
  price: '',
  displayNumber: '',
  minQuantity: '',
  productId: '',
  gstFree: false,
  ingredients: '',
  isOpenTableItem: false,
  isOpenTableSharedItem: false,
  colorHex: null,
  printCategories: [],
  sizes: [],
});

export const dishFormFrom = (item) => ({
  ...emptyDishForm(),
  name: item.name || '',
  altName: item.altName || '',
  shortName: item.shortName || '',
  description: item.description || '',
  price: item.price ?? '',
  displayNumber: item.displayNumber || '',
  minQuantity: item.minQuantity ?? '',
  productId: item.productId || '',
  gstFree: !!item.gstFree,
  ingredients: item.ingredients || '',
  isOpenTableItem: !!item.isOpenTableItem,
  isOpenTableSharedItem: !!item.isOpenTableSharedItem,
  colorHex: item.colorHex || null,
  sizes: item.sizes || [],
});

const pricedSizes = (sizes) => (sizes || []).map((size) => ({ ...size, price: Number(size.price) }));

/** Every size needs a name and a price before a dish can be saved. */
export const sizesComplete = (sizes) =>
  (sizes || []).every(
    (size) => size.name !== undefined && size.name !== '' && size.price !== undefined && size.price !== ''
  );

/** POST /menuItem, as NewMenuItem sends it (the thunk adds the id). */
export const newDishPayload = (form, menuId, categoryId) => ({
  name: form.name,
  description: form.description,
  altName: form.altName,
  shortName: form.shortName,
  price: Number(form.price),
  sort: 999,
  menuId,
  categoryId,
  active: true,
  active_begin: null,
  active_end: null,
  hidden_until: null,
  tags: [],
  groups: [],
  sizes: pricedSizes(form.sizes),
  productId: form.productId,
  ingredients: form.ingredients,
  gstFree: form.gstFree,
  displayNumber: form.displayNumber,
  cookingCategory: null,
  printCategory: joinPrintCategories(form.printCategories),
  isOpenTableItem: form.isOpenTableItem,
  isOpenTableSharedItem: form.isOpenTableSharedItem,
});

/** The dish as MenuItemEdit saves it. */
export const editedDish = (item, form, menuId, categoryId) => ({
  ...item,
  name: form.name,
  description: form.description,
  altName: form.altName,
  shortName: form.shortName,
  price: Number(form.price),
  sizes: pricedSizes(form.sizes),
  productId: form.productId,
  ingredients: form.ingredients,
  gstFree: form.gstFree,
  displayNumber: form.displayNumber,
  isOpenTableItem: form.isOpenTableItem,
  isOpenTableSharedItem: form.isOpenTableSharedItem,
  colorHex: form.colorHex,
  minQuantity: form.minQuantity ? Number(form.minQuantity) : null,
  categoryId,
  menuId,
});

/**
 * A copy of the dish for POST /menuItem. Unlike the classic editor this leaves out `image`: the copy
 * has no photo of its own, and carrying the original's URL lets a later upload on the copy delete
 * the original's photo file.
 */
export const duplicateDishPayload = (item) => {
  const copy = { ...item, name: `Copy of ${item.name}` };
  delete copy.image;
  return copy;
};

/** POST /menuItems body that replaces one dish in its category. */
export const itemsWithDish = (category, dish) => ({
  items: (category.items || []).map((item) => (item.id === dish.id ? dish : item)),
  menuId: dish.menuId,
  categoryId: category.id,
});

export const itemsWithoutDish = (category, dishId, menuId) => ({
  items: (category.items || []).filter((item) => item.id !== dishId),
  menuId,
  categoryId: category.id,
});

export const itemsInOrder = (category, from, to, menuId) => ({
  items: reorderWithSort(category.items || [], from, to),
  menuId,
  categoryId: category.id,
});

export const itemsWithDishOnTop = (category, index, menuId) => itemsInOrder(category, index, 0, menuId);

/** Attaches a choice group by reference, as dropping a group on a dish does. */
export const withGroup = (groups, groupId) =>
  (groups || []).some((group) => group.id === groupId) ? groups : [...(groups || []), { id: groupId, sort: 999 }];

export const withoutGroup = (groups, groupId) => (groups || []).filter((group) => group.id !== groupId);

export const groupsInOrder = (groups, from, to) => reorderWithSort(groups || [], from, to);

/** The same four kitchen flags written onto every dish in a category (MenuMiscSettings, category mode). */
export const itemsWithKitchenFlags = (category, flags, menuId) => ({
  items: (category.items || []).map((item) => ({ ...item, ...flags })),
  menuId,
  categoryId: category.id,
});

export const itemsWithPatch = (category, patch, menuId) => ({
  items: (category.items || []).map((item) => ({ ...item, ...patch })),
  menuId,
  categoryId: category.id,
});

/** CategoryOptions' promotion tags: replace the category's previous tags on every dish. */
export const itemsWithCategoryPromotionTags = (category, newTags, menuId) => {
  const previous = category.promotionTags || [];
  return {
    items: (category.items || []).map((item) => {
      const kept = (item.promotionTags || []).filter((tag) => !previous.includes(tag));
      return { ...item, promotionTags: [...new Set([...kept, ...newTags])] };
    }),
    menuId,
    categoryId: category.id,
    promotionTags: newTags,
  };
};

export const itemsWithoutPromotionTags = (category, menuId) => ({
  items: (category.items || []).map((item) => ({ ...item, promotionTags: [] })),
  menuId,
  categoryId: category.id,
  promotionTags: [],
});

/** PUT /myMenu body that adds any new promotion tags to the menu's list. */
export const menuWithPromotionTags = (menu, tags) => ({
  ...menu,
  promotionTags: [...new Set([...((menu && menu.promotionTags) || []), ...tags])],
});

export const toggleTag = (tags, tag, on) => {
  const others = (tags || []).filter((value) => value.toLowerCase() !== tag.toLowerCase());
  return on ? [...others, tag] : others;
};

/** Tag values were saved with varying case over time; match them case-insensitively. */
export const hasTag = (tags, tag) => (tags || []).some((value) => value.toLowerCase() === tag.toLowerCase());

// ---------------------------------------------------------------------------------------------
// Choice groups and options

export const newGroupPayload = (form, menuId, sort) => ({
  menuId,
  name: form.name,
  required: form.required,
  allow_quantity: form.allow_quantity,
  force_min: Number(form.force_min),
  force_max: Number(form.force_max),
  sort: Number(sort),
  hideForWeb: form.hideForWeb,
});

/**
 * The sort for a new group: after every existing one. The classic editor bumped colliding sorts and
 * sent the new group through PUT /menuGroups, which rejects a group without an id (and only exists on
 * one of the backends), so on a collision the group was never created.
 */
export const newGroupSort = (groups) =>
  (groups || []).reduce((max, group) => Math.max(max, Number(group.sort) || 0), 0) + 1;

export const editedGroupPayload = (group, form, menuId) => {
  const options = form.colorHex
    ? (group.options || []).map((option) => ({ ...option, colorHex: form.colorHex }))
    : group.options || [];
  return {
    ...group,
    name: form.name,
    required: form.required,
    allow_quantity: form.allow_quantity,
    force_min: Number(form.force_min),
    force_max: Number(form.force_max),
    sort: Number(form.sort),
    hideForWeb: form.hideForWeb,
    colorHex: form.colorHex,
    menuId,
    options,
  };
};

/** A copy of a group: POST /menuGroup without the source id, then its options under the new id. */
export const copiedGroupPayload = (group) => {
  const copy = { ...group, name: `copy of ${group.name}` };
  delete copy.id;
  delete copy.options;
  return copy;
};

export const copiedGroupOptions = (group, newGroupId) =>
  (group.options || []).map((option) => ({ ...option, id: uuid(), groupId: newGroupId }));

export const newOptionPayload = (form, group) => ({
  name: form.name,
  shortName: form.shortName,
  price: Number(form.price),
  groupId: group.id,
  menuId: group.menuId,
  default: form.default,
  sort: group.options ? group.options.length + 1 : 1,
  hideForWeb: form.hideForWeb,
});

export const linkedOptionPayload = (form, group, link) => ({
  name: form.name,
  price: Number(form.price),
  shortName: form.shortName,
  groupId: group.id,
  menuId: group.menuId,
  default: form.default,
  sort: 999,
  hideForWeb: form.hideForWeb,
  isPrintDisabled: form.isPrintDisabled,
  menuItemId: link.menuItemId,
  menuItemName: link.menuItemName,
  category: link.category,
});

/** Older options may have no id, so match the very object first and fall back to a shared id. */
export const sameOption = (a, b) => a === b || (!!a && !!b && a.id != null && a.id === b.id);

/** PUT /menuGroup body after editing one option, as GroupOptionEdit sends it. */
export const groupWithEditedOption = (group, option, form, menuId) => ({
  ...group,
  options: (group.options || []).map((entry) =>
    sameOption(entry, option)
      ? {
          ...option,
          name: form.name,
          shortName: form.shortName,
          price: Number(form.price),
          groupId: group.id,
          menuId: group.menuId,
          default: form.default,
          hideForWeb: form.hideForWeb,
          colorHex: form.colorHex,
        }
      : entry
  ),
  menuId,
});

export const copiedOptionPayload = (option, group, menuId) => ({
  ...option,
  name: `copy of ${option.name}`,
  sort: (group.options || []).length + 1,
  menuId,
  groupId: group.id,
  id: uuid(),
});

export const optionsInOrder = (options, from, to) => reorderWithSort(options || [], from, to);

export const optionsWithout = (options, removed) => (options || []).filter((option) => !sameOption(option, removed));

/** How a linked option relates to the dishes on the menu. */
export const optionLink = (option, categories) => {
  if (!option.menuItemId) {
    return null;
  }
  const found = (categories || []).some((category) =>
    (category.items || []).some((item) => item.id === option.menuItemId)
  );
  if (!found) {
    return { broken: true, label: 'broken link' };
  }
  return { broken: false, label: option.category ? `link · ${option.category}` : 'link' };
};

/**
 * "Fix broken links": point each linked option at the first dish with the same name. Like the classic
 * editor, this relinks any option whose name matches a different dish, broken or not.
 */
export const relinkedOptions = (options, categories) => {
  let fixed = 0;
  const next = (options || []).map((option) => {
    if (!option.menuItemId) {
      return option;
    }
    for (const category of categories || []) {
      for (const item of category.items || []) {
        if (option.name === item.name && option.menuItemId !== item.id) {
          fixed += 1;
          return { ...option, menuItemId: item.id, category: category.name };
        }
      }
    }
    return option;
  });
  return { options: next, fixed };
};
